import {
  FormControl, InputLabel, makeStyles, MenuItem, Select, TextField,
} from '@material-ui/core';
import moment from 'moment';
import { ChangeEvent } from 'react';

import { INVOICE_TEMPLATE_ALLOWED_FORMATS } from '../../constants/invoice-template-allowed-formats.constant';
import { InvoiceFormat, InvoiceFormats } from '../../types/invoice-format.type';
import { InvoiceTemplateId, InvoiceTemplateIds } from '../../types/invoice-template-id.type';

export type InvoiceGenerationSettings = {
  templateId: InvoiceTemplateId;
  format: InvoiceFormat;
  from: string;
  to: string;
};

type Props = {
  value: InvoiceGenerationSettings;
  onChange: (next: InvoiceGenerationSettings) => void;
};

// Intervalle par défaut visé : M-2, comportement conservé depuis la modale segment.
function getDefaultBillingInterval(): { from: string; to: string } {
  const lastMonth = moment().subtract(2, 'month');
  return {
    from: lastMonth.startOf('month').format('YYYY-MM-DD'),
    to: lastMonth.endOf('month').format('YYYY-MM-DD'),
  };
}

export function getDefaultInvoiceGenerationSettings(): InvoiceGenerationSettings {
  const defaultInterval = getDefaultBillingInterval();
  return {
    templateId: InvoiceTemplateIds.STANDARD,
    format: InvoiceFormats.PDF,
    from: defaultInterval.from,
    to: defaultInterval.to,
  };
}

export function hasInvalidInterval(settings: InvoiceGenerationSettings): boolean {
  return Boolean(settings.from && settings.to) && settings.from > settings.to;
}

const useStyles = makeStyles((theme) => ({
  rowsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    paddingTop: '0.5rem',

    '& .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.admin.primary.main,
    },
    '& .MuiInputLabel-outlined.Mui-focused:not(.Mui-error)': {
      color: theme.palette.admin.primary.main,
    },
  },
  flexRow: {
    display: 'flex',
    gap: '1rem',
    '& > *': { flex: 1 },
  },
}));

export function InvoiceGenerationSettingsFields({ value, onChange }: Props) {
  const classes = useStyles();
  const { templateId, format, from, to } = value;

  const allowedFormats = INVOICE_TEMPLATE_ALLOWED_FORMATS[templateId];
  const hasIntervalError = hasInvalidInterval(value);

  const handleTemplateChange = (event: ChangeEvent<{ value: unknown }>) => {
    const nextTemplateId = event.target.value as InvoiceTemplateId;
    const nextAllowedFormats = INVOICE_TEMPLATE_ALLOWED_FORMATS[nextTemplateId];
    const nextFormat = nextAllowedFormats.includes(format) ? format : nextAllowedFormats[0];
    onChange({ ...value, templateId: nextTemplateId, format: nextFormat });
  };

  const handleFromChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextFrom = event.target.value;
    const nextTo = nextFrom && to && nextFrom > to
      ? moment(nextFrom).endOf('month').format('YYYY-MM-DD')
      : to;
    onChange({ ...value, from: nextFrom, to: nextTo });
  };

  const handleToChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, to: event.target.value });
  };

  return (
    <div className={classes.rowsContainer}>
      <div className={classes.flexRow}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel>Template</InputLabel>
          <Select
            label="Template"
            value={templateId}
            onChange={handleTemplateChange}
          >
            <MenuItem value={InvoiceTemplateIds.STANDARD}>Standard</MenuItem>
            <MenuItem value={InvoiceTemplateIds.ADVANCED}>Advanced</MenuItem>
            <MenuItem value={InvoiceTemplateIds.EXPERT}>Expert</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" fullWidth>
          <InputLabel>Format</InputLabel>
          <Select
            label="Format"
            value={format}
            onChange={(event) => onChange({ ...value, format: event.target.value as InvoiceFormat })}
          >
            {allowedFormats.map((allowed) => (
              <MenuItem key={allowed} value={allowed}>{allowed}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className={classes.flexRow}>
        <TextField
          variant="outlined"
          fullWidth
          label="Du"
          type="date"
          value={from}
          onChange={handleFromChange}
          InputLabelProps={{ shrink: true }}
          error={hasIntervalError}
          helperText={hasIntervalError ? 'La date "Du" doit être avant ou égale à "Au"' : undefined}
        />
        <TextField
          variant="outlined"
          fullWidth
          label="Au"
          type="date"
          value={to}
          onChange={handleToChange}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: from }}
          error={hasIntervalError}
          helperText={hasIntervalError ? 'La date "Au" doit être après ou égale à "Du"' : undefined}
        />
      </div>
    </div>
  );
}
